/**
 * Privacy Policy + Terms of Service.
 *
 * These are DRAFTS authored without legal counsel. They are intentionally
 * conservative and aim to (a) satisfy Telegram BotFather's Privacy Policy URL
 * requirement, (b) give beta users a reasonable read on how data is handled,
 * and (c) provide the cost-share-not-transport positioning that's central to
 * Waylo's legal armor. A lawyer must review before public launch.
 *
 * Single component rendered at /privacy and /terms — anchor links jump
 * within the page so the two policies are co-located and short.
 */
import { BackButton } from '../lib/BackButton';
import { useRouter } from '../lib/router';

const LAST_UPDATED = '2026-05-18';
const CONTACT_EMAIL = 'support@waylo.app';
const OPERATOR = 'Triardor d.o.o.';

interface Props {
  /** Which page is "primary" — used to pick the page header. */
  variant: 'privacy' | 'terms';
}

export function LegalPage({ variant }: Props): JSX.Element {
  const { navigate } = useRouter();
  return (
    <div className="mx-auto max-w-md px-4 py-6">
      <BackButton />

      <article className="prose prose-sm max-w-none text-tg-text">
        <header className="mb-4">
          <h1 className="text-2xl font-bold text-tg-text">
            {variant === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
          </h1>
          <p className="text-xs text-tg-hint">
            Last updated: {LAST_UPDATED} · Operator: {OPERATOR}
          </p>
          <p className="mt-2 rounded-card bg-[var(--warning)]/10 p-3 text-xs text-[var(--warning)]">
            <strong>Draft.</strong> This document is a draft for beta. We're refining it
            with legal counsel ahead of public launch. If you spot anything inaccurate,
            email {CONTACT_EMAIL}.
          </p>
        </header>

        {variant === 'privacy' ? <PrivacyContent /> : <TermsContent />}

        <footer className="mt-8 border-t border-[var(--border-light)] pt-4 text-xs text-tg-hint">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate(variant === 'privacy' ? '/terms' : '/privacy');
            }}
            className="text-tg-link underline"
          >
            {variant === 'privacy' ? 'Read the Terms of Service' : 'Read the Privacy Policy'} →
          </a>
          <p className="mt-3">Contact: {CONTACT_EMAIL}</p>
          <p className="mt-2">
            <a
              href="https://triardor.com/en/"
              target="_blank"
              rel="noreferrer"
              className="text-tg-link underline"
            >
              Made by Triardor
            </a>
          </p>
        </footer>
      </article>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Privacy Policy
// ---------------------------------------------------------------------------

function PrivacyContent(): JSX.Element {
  return (
    <>
      <Section title="1. Who we are">
        <p>
          Waylo is a community noticeboard for sharing private intercity car trips. It is
          operated by {OPERATOR} (Belgrade, Serbia) and runs as a Telegram Mini App
          published by the bot <code>@WayloRide_bot</code>.
        </p>
        <p>
          Waylo is <strong>not a transport service or carrier</strong>. We connect people
          who are already travelling between cities so they can share the real cost of
          the trip. Rides themselves are not arranged, supervised, or insured by Waylo.
        </p>
      </Section>

      <Section title="2. What we collect">
        <p>When you use Waylo, we collect and store:</p>
        <ul>
          <li>
            <strong>Telegram account data</strong> Telegram exposes to Mini Apps: your
            numeric user id, first/last name, optional <code>@username</code>, language
            code, premium status flag, and your profile photo URL (if public).
          </li>
          <li>
            <strong>Profile data you enter</strong> in Waylo: display name override,
            bio, car make/model/colour/plate (drivers only), travel preferences ("vibes"),
            language, country, optional phone number, optional emergency contact, your
            chosen visibility flags.
          </li>
          <li>
            <strong>Activity data</strong> generated by your use of Waylo: rides you publish,
            seats you request, bookings you approve or decline, ratings you give and receive,
            reports filed by or against you, premium subscription history.
          </li>
          <li>
            <strong>Technical data</strong> needed to keep the service running: your IP
            address (for rate limiting and abuse detection), HTTP request logs, anti-abuse
            flags.
          </li>
        </ul>
        <p>
          We do <strong>not</strong> collect: government-issued IDs, payment-card numbers
          (premium is paid in Telegram Stars and handled entirely by Telegram), driver's
          licence images, vehicle insurance details, or location/GPS data.
        </p>
      </Section>

      <Section title="3. How we use it">
        <ul>
          <li>To match riders with rides on the routes you've searched.</li>
          <li>To send you booking notifications via the bot.</li>
          <li>To enforce the anti-abuse rules that keep Waylo non-commercial (see Terms §3).</li>
          <li>To compute ratings and trust badges on your public profile.</li>
          <li>To detect and respond to abuse, fraud, or safety reports.</li>
          <li>To provide customer support when you email {CONTACT_EMAIL}.</li>
        </ul>
        <p>
          We do <strong>not</strong> use your data for advertising, profiling, or training
          AI models. We do not sell your data.
        </p>
      </Section>

      <Section title="4. What we share, with whom, and when">
        <p>
          Waylo's privacy model is "share only after a confirmed match, and only with
          consent." Specifically:
        </p>
        <ul>
          <li>
            <strong>Search results show:</strong> the driver's display name, profile photo,
            rating average, completed ride count, trust badges, and the ride details (city
            pair, departure time, seats, cost, preferences). <em>Nothing else.</em>
          </li>
          <li>
            <strong>After a booking is approved</strong> AND the user has opted in,
            the counterparty sees the other party's Telegram <code>@username</code> and/or
            phone number. These are visible only to that specific counterparty for that
            specific trip.
          </li>
          <li>
            <strong>Public profile</strong> shows your display name, photo, ratings, trust
            badges, bio (if you set bio_visible=true) and travel vibes. Nothing else.
          </li>
          <li>
            <strong>Reports you file</strong> are visible only to you and Waylo moderators.
          </li>
          <li>
            <strong>Safety alerts</strong> you trigger via the in-app safety button are
            sent to Waylo's operational channel and (with your consent) to your registered
            emergency contact.
          </li>
        </ul>
      </Section>

      <Section title="5. Where your data lives">
        <p>
          User data, rides, bookings, ratings and reports are stored in a Cloudflare D1
          database hosted in the European Union (EEUR region — Warsaw, Poland). The
          worker that processes requests runs on Cloudflare's global edge network with
          requests typically served from Belgrade or Frankfurt for Balkans users.
        </p>
        <p>
          Telegram messages sent by the bot transit Telegram's infrastructure (Pavel
          Durov, registered in the British Virgin Islands; operational presence in
          Dubai). Telegram's privacy policy applies separately: see
          <a href="https://telegram.org/privacy"> telegram.org/privacy</a>.
        </p>
      </Section>

      <Section title="6. Retention">
        <ul>
          <li>
            <strong>Soft-deleted users:</strong> if you delete your account, your row is
            marked <code>deleted_at</code> and excluded from all searches and profiles
            within 24 hours. Your data is hard-deleted after 90 days unless we are
            obliged to retain longer for an ongoing abuse investigation.
          </li>
          <li>
            <strong>Rides and bookings:</strong> retained for 24 months after departure,
            then hard-deleted. We keep an aggregate ride count on your profile for the
            life of your account.
          </li>
          <li>
            <strong>Audit log:</strong> retained 24 months for fraud and abuse investigation.
          </li>
          <li>
            <strong>Notifications:</strong> outbox rows are purged 60 days after they
            are sent.
          </li>
        </ul>
      </Section>

      <Section title="7. Your rights">
        <p>
          You can exercise the following rights at any time by emailing {CONTACT_EMAIL}:
        </p>
        <ul>
          <li>Export a JSON copy of all data we hold about you.</li>
          <li>Correct any inaccurate information in your profile.</li>
          <li>Delete your account (soft delete now, hard delete in 90 days).</li>
          <li>Withdraw consent for phone or Telegram username sharing.</li>
          <li>
            Object to processing — note that opting out of anti-abuse rules will mean we
            cannot operate the service for you.
          </li>
        </ul>
        <p>
          We respond to data subject requests within 30 days as required by the Serbian
          ZZPL (Закон о заштити података о личности) and the EU GDPR where applicable.
        </p>
      </Section>

      <Section title="8. Cookies and similar technologies">
        <p>
          The Waylo Mini App stores one HTTP-only cookie called <code>waylo_session</code>
          for authentication (lifetime 7 days). It also writes one entry to{' '}
          <code>localStorage</code> (<code>waylo_session</code>) as a fallback when
          third-party cookies are blocked. We do not use analytics cookies, tracking
          pixels, or third-party advertising tags.
        </p>
      </Section>

      <Section title="9. Changes to this policy">
        <p>
          We will update this page when we change how data is handled and announce the
          change inside the Mini App. Major changes will be notified to all active users
          via the bot.
        </p>
      </Section>

      <Section title="10. Contact and complaints">
        <p>
          For any privacy question or complaint, email {CONTACT_EMAIL}. If you are not
          satisfied with our response you may file a complaint with the Serbian
          Commissioner for Information of Public Importance and Personal Data Protection
          (<a href="https://www.poverenik.rs">poverenik.rs</a>) or your local EU data
          protection authority.
        </p>
      </Section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Terms of Service
// ---------------------------------------------------------------------------

function TermsContent(): JSX.Element {
  return (
    <>
      <Section title="1. What Waylo is">
        <p>
          Waylo is a community noticeboard for sharing the real cost of private
          intercity car trips. Drivers publish trips they are already making. Passengers
          travelling the same way share the cost. Waylo is not a taxi service, a
          carpooling marketplace with profit margins, a logistics company, or a
          transport carrier.
        </p>
        <p>
          By using Waylo you agree to use it for genuine cost-sharing only. Commercial
          use is prohibited and enforced (see §3 below).
        </p>
      </Section>

      <Section title="2. Who can use Waylo">
        <ul>
          <li>You must be at least 18 years old.</li>
          <li>You must have a valid Telegram account.</li>
          <li>If you publish rides as a driver, you must hold a valid driving licence
          for the country in which you drive and the vehicle must be roadworthy and
          insured for passenger transport in your jurisdiction.</li>
          <li>You agree to use truthful information on your profile.</li>
          <li>You agree not to share your account with anyone else.</li>
        </ul>
      </Section>

      <Section title="3. Anti-abuse rules (these are not negotiable)">
        <p>
          These rules are what keeps Waylo legal as a community platform. They apply
          automatically:
        </p>
        <ul>
          <li>
            <strong>Price guideline (AA-02).</strong> The reference cost shown for each
            route is a community guideline. Drivers may publish below it, at it, or
            above. Prices above 1.5× the reference trigger an in-app advisory and an
            internal audit record, but the ride still goes live — the market decides.
          </li>
          <li>
            <strong>Soft price warning (AA-03).</strong> Drivers with fewer than three
            completed rides pricing above 1.2× the reference get the same in-app
            advisory plus an internal audit record. The ride publishes normally.
          </li>
          <li>
            <strong>Frequency cap (AA-01).</strong> A single driver cannot publish more
            than three rides on the same route in 24 hours. This is the platform's
            anti-commercial-transport defense — exceeding it blocks publish until the
            24-hour window rolls forward.
          </li>
          <li>
            <strong>Reports cap (AA-04).</strong> Three or more open user reports against
            you within 14 days result in automatic suspension pending moderator review.
          </li>
        </ul>
        <p>
          Waylo will refuse service to anyone who attempts to use it as a commercial
          taxi, ride-hail, or scheduled-bus alternative. Decisions on borderline cases
          rest with Waylo's moderators.
        </p>
      </Section>

      <Section title="4. Ratings and reports">
        <ul>
          <li>You can rate and be rated after a completed ride. Ratings are public on profiles.</li>
          <li>
            Reports are confidential — visible to you (the reporter) and Waylo moderators
            only. Filing false reports is a violation of these terms and grounds for
            suspension.
          </li>
          <li>
            You can block other users. Blocked users cannot book your rides and vice versa.
          </li>
        </ul>
      </Section>

      <Section title="5. Payments">
        <p>
          Waylo is free to use. There is no commission on rides. Cost-sharing payments
          between driver and passenger happen offline — Waylo does not handle that money.
        </p>
        <p>
          Waylo Premium (50 Telegram Stars / 30 days at the time of writing) unlocks
          unlimited ride publishing and a "Founding Member" badge. Premium is paid in
          Telegram Stars, an in-app currency handled entirely by Telegram. We never see
          your card number.
        </p>
      </Section>

      <Section title="6. Liability">
        <p>
          Waylo is a noticeboard. We do not provide the transport service itself. We do
          not vet drivers, inspect vehicles, verify insurance, or supervise rides. By
          using Waylo, you acknowledge that:
        </p>
        <ul>
          <li>
            Sharing a private vehicle with strangers carries inherent risks. Use Waylo's
            in-app safety button and emergency contact features.
          </li>
          <li>
            Waylo's liability is limited to the maximum extent permitted by law and in
            no case exceeds the amount you have paid to Waylo (which is typically zero).
          </li>
          <li>
            Disputes between drivers and passengers (e.g. about pricing, no-shows,
            damaged property) are resolved between those parties. Waylo's moderators
            may mediate but cannot guarantee outcomes.
          </li>
          <li>
            Insurance for the trip is the responsibility of the vehicle owner (typically
            the driver's standard third-party liability cover, which in Serbia and the
            wider Balkans covers passengers as standard).
          </li>
        </ul>
      </Section>

      <Section title="7. Account termination">
        <p>
          We can suspend or terminate your account if you violate these terms. Common
          reasons include: commercial use, abusive behaviour towards other users,
          fraudulent payment activity, or three or more unresolved reports.
        </p>
        <p>You can delete your account at any time from the Profile screen.</p>
      </Section>

      <Section title="8. Changes to these terms">
        <p>
          We will update these terms when we change how the service operates. Material
          changes will be announced inside the Mini App. Continued use after the change
          constitutes acceptance.
        </p>
      </Section>

      <Section title="9. Governing law">
        <p>
          These terms are governed by the laws of the Republic of Serbia. Disputes that
          cannot be resolved amicably will be heard by the competent court in Belgrade.
        </p>
      </Section>

      <Section title="10. Contact">
        <p>{CONTACT_EMAIL}</p>
      </Section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className="mb-5">
      <h2 className="mb-2 text-base font-semibold text-tg-text">{title}</h2>
      <div className="space-y-2 text-sm text-tg-text">{children}</div>
    </section>
  );
}
